import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { ListsDataForHomePage } from 'src/app/lists/model/lists-page.model';
import { PATHS } from 'src/app/shared/constants/paths';
import { NavigationHelperService } from 'src/app/shared/helpers/navigation.helpers.service';
import { ListsOverlayService } from '../../../lists/services/lists-overlay.service';
import { TileTypes } from '../../../shared/constants/home-constants';
import { RecommendedContent } from '../../models/tile.model';
import { AppService } from '@shared/services/app.service';
import { ListsService } from '../../../lists/services/lists.service';
import { ProductAnalyticsService } from '@usf/ngrx-product';
import { TopSectionSearchText } from '@search/shared/models/search-page.model';
import { combineLatest, Subscription } from 'rxjs';
import { FEATURES } from '@shared/constants/splitio-features';
import { UserService } from '@app/user/services';
import { ViewWillLeave } from '@ionic/angular';

@Component({
  selector: 'app-base-tile',
  templateUrl: './base-tile.component.html',
  styleUrls: ['./base-tile.component.scss'],
})
export class BaseTileComponent implements ViewWillLeave {
  @Input() type?: string;
  @Input() id: string;
  @Input() title?: string;
  @Input() newsTitle?: string;
  @Input() textBody?: string;
  @Input() newsBody?: string;
  @Input() textButton?: string;
  @Input() newsButton?: string;
  @Input() newsUrl?: string;
  @Input() backgroundImage?: string;
  @Input() path?: string[];
  @Input() deliveryDate?: string;
  @Input() deliveryTime?: string;
  @Input() dtDescription?: string;
  @Input() isDelivered?: boolean;
  @Input() lists?: any[];
  @Input() listsData?: ListsDataForHomePage;
  @Input() recommendedContent?: RecommendedContent;
  @Input() customerTimeZone?: string;
  @Input() totalAnnualSavings?: number;
  @Input() hideEtaRange?: boolean;
  @Input() confirmedDeliveryDate?: Date | string;
  @Input() etaReasonShort?: string;
  @Input() etaReasonLong?: string;

  isDesktop =
    this.platformService.platformType ===
    this.platformService.platformEnum.desktop;
  isMobile =
    this.platformService.platformType ===
    this.platformService.platformEnum.mobile;
  isGuestUserAndSearchBrowseFeatureFlagSub$: Subscription;

  constructor(
    private router: Router,
    private translateService: TranslateService,
    readonly platformService: PlatformService,
    private listsOverlayService: ListsOverlayService,
    private navigationHelper: NavigationHelperService,
    private appService: AppService,
    private listsService: ListsService,
    private analyticsService: ProductAnalyticsService,
    readonly panAppState: PanAppState,
    private userService: UserService,
  ) {}

  ionViewWillLeave() {
    if (this.isGuestUserAndSearchBrowseFeatureFlagSub$) {
      this.isGuestUserAndSearchBrowseFeatureFlagSub$.unsubscribe();
    }
  }

  openListDetails(listId: string, listName: string) {
    if (
      listName === this.translateService.instant('i18n.lists.recentlyPurchased')
    ) {
      this.routeTo([PATHS.LISTS, 'recentlyPurchased']);
    } else if (listId.slice(0, 2) === 'ML') {
      this.routeTo([PATHS.LISTS, 'masterList', listId]);
    } else {
      this.listsService.fetchListChanges();
      this.routeTo([PATHS.LISTS, 'detail', listId]);
    }
  }

  async openCreateListModal(listData: ListsDataForHomePage) {
    this.listsOverlayService.openCreateListModal(listData);
    this.routeTo([PATHS.LISTS]);
  }

  routeTo(path?: string[]) {
    if (this.type === TileTypes.report) {
      this.appService.openUrlInBrowser(path[0]);
    } else if (this.type === TileTypes.wmt) {
      this.router.navigate([...path], {
        queryParams: { lnksrc: 'wheresMyTruck' },
      });
    } else if (this.type === TileTypes.recommended) {
      if (path[0] === '') {
        this.navigationHelper.routeTo(PATHS.NOTIFICATIONS, {
          tmnotes: true,
        });
      } else {
        this.navigationHelper.routeToNoteDetails(+path[0]);
      }
    } else if (this.type === TileTypes.deals) {
      this.router.navigate([...path], {
        queryParams: { lnksrc: 'deals' },
      });
    } else if (this.type === TileTypes.news) {
      window.open(this.newsUrl, '_blank');
    } else if (this.type === TileTypes.equipmentSuppliesTile) {
      this.analyticsService.trackEquipmentAndSupplies({
        buttonName: 'equipment & supplies:home page tile',
        promoId: 'EandSCoreEssentials',
      });
      this.router.navigate([...path], {
        queryParams: {
          searchText: TopSectionSearchText.EQUIPMENT_AND_SUPPLIES,
        },
      });
    } else if (this.type === TileTypes.browse) {
      this.isGuestUserAndSearchBrowseFeatureFlagSub$ = combineLatest([
        this.panAppState.feature$([FEATURES.split_global_search_browse.name]),
        this.userService.isGuestUser$(),
      ]).subscribe(([featureFlag, isGuestUser]) => {
        if (featureFlag || isGuestUser) {
          this.navigationHelper.routeTo(PATHS.SEARCH_BROWSE);
        } else {
          this.navigationHelper.routeTo(PATHS.SEARCH_CATALOG);
        }
      });
    } else {
      this.router.navigate([...path]);
    }
  }
}
